import { useEffect, useState } from "react";
import { X, Check, ArrowClockwise, Sparkle, MagicWand } from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";
import { CategoryIcon } from "./CategoryManagerDialog";

/**
 * Auto-suggest similar-transactions dialog.
 *
 * Trigger: opens after the user recategorizes a SINGLE transaction. Backend
 * `/finance/transactions/find-similar` scores other rows by:
 *   1. Exact merchant match  -> score 1.0
 *   2. Token overlap Jaccard -> 0.5..0.99
 *
 * The user can:
 *   - check/uncheck individual rows,
 *   - "Seleccionar todo" / "Quitar todo",
 *   - "Aplicar categoría" (single bulk-update call with a learned rule),
 *   - close/cancel without changing anything.
 */
export default function SimilarSuggestionDialog({
  open,
  transactionId,
  prefetched,       // { source, matches, total_matches }  optional — skips the fetch
  category,      // NEW category to apply (e.g. "food")
  categoryLabel, // Human-readable label ("Comida")
  categoryColor, // Color hex from category
  categoryIcon,  // Icon name from category
  categoryLookup, // byKey map { <key>: {label, color, icon} } for row translations
  onClose,
  onApplied,     // (count) => void  invoked after a successful bulk-apply
}) {
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null);
  const [matches, setMatches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (!open || !transactionId) return;
    // If parent already fetched, use it directly
    if (prefetched?.matches) {
      setSource(prefetched.source);
      setMatches(prefetched.matches);
      setSelected(new Set(
        prefetched.matches
          .filter((m) => m.reason === "merchant")
          .map((m) => m.transaction_id),
      ));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setMatches([]);
    setSelected(new Set());
    (async () => {
      try {
        const { data } = await api.post("/finance/transactions/find-similar", {
          transaction_id: transactionId,
        });
        if (cancelled) return;
        setSource(data.source);
        setMatches(data.matches || []);
        const preselect = new Set(
          (data.matches || [])
            .filter((m) => m.reason === "merchant")
            .map((m) => m.transaction_id),
        );
        setSelected(preselect);
      } catch (err) {
        toast.error("No se pudieron buscar transacciones similares");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, transactionId, prefetched]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const selectAll = () => setSelected(new Set(matches.map((m) => m.transaction_id)));
  const clearAll = () => setSelected(new Set());

  const applyCategory = async () => {
    if (selected.size === 0) {
      toast.info("Selecciona al menos una transacción");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/finance/transactions/bulk-update", {
        transaction_ids: Array.from(selected),
        category,
        learn: false,   // do NOT create another learned rule (the single-put already did)
      });
      toast.success(`${data.updated ?? selected.size} transacciones actualizadas`);
      onApplied?.(data.updated ?? selected.size);
      onClose?.();
    } catch (err) {
      toast.error("No se pudieron aplicar los cambios");
    } finally {
      setApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="similar-suggestion-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        {/* Header */}
        <div className="p-5 border-b border-white/8 flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#FACC15] flex items-center gap-1.5">
              <MagicWand size={12} weight="bold" /> SUGERENCIA · CATEGORIZACIÓN
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              ¿Aplicar la misma categoría a transacciones similares?
            </div>
            {source && (
              <div className="mt-2 flex items-center gap-2 text-[12px] font-mono text-white/60">
                <span>Basado en:</span>
                <span className="px-2 py-0.5 rounded bg-white/6 text-white font-bold">
                  {source.merchant || source.description}
                </span>
                <span>→</span>
                <span
                  className="on-solid inline-flex items-center gap-1 px-2 py-0.5 rounded font-bold text-white"
                  style={{ backgroundColor: categoryColor || "#94A3B8" }}
                >
                  <CategoryIcon name={categoryIcon || "Tag"} size={10} weight="fill" />
                  {categoryLabel}
                </span>
              </div>
            )}
          </div>
          <button
            data-testid="similar-close"
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center shrink-0"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="py-16 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={20} weight="bold" className="animate-spin mx-auto mb-2" />
              Buscando transacciones similares...
            </div>
          ) : matches.length === 0 ? (
            <div className="py-16 text-center text-xs font-mono text-white/40">
              <Sparkle size={20} weight="fill" className="text-[#FACC15] mx-auto mb-2" />
              No encontramos otras transacciones similares.
            </div>
          ) : (
            <>
              <div className="px-5 py-2 border-b border-white/6 flex items-center justify-between">
                <div className="text-[11px] font-mono uppercase tracking-widest text-white/50">
                  {matches.length} coincidencia(s) — {selected.size} seleccionada(s)
                </div>
                <div className="flex items-center gap-1">
                  <button
                    data-testid="similar-select-all"
                    onClick={selectAll}
                    className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest text-white/70 hover:bg-white/6"
                  >
                    Seleccionar todo
                  </button>
                  <button
                    data-testid="similar-clear-all"
                    onClick={clearAll}
                    className="px-2 py-1 rounded text-[10px] font-mono uppercase tracking-widest text-white/50 hover:bg-white/6"
                  >
                    Quitar todo
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-white/6">
                {matches.map((m) => {
                  const checked = selected.has(m.transaction_id);
                  return (
                    <li
                      key={m.transaction_id}
                      data-testid={`similar-row-${m.transaction_id}`}
                      className={`px-5 py-3 flex items-center gap-3 cursor-pointer transition ${
                        checked ? "bg-emerald-500/8 hover:bg-emerald-500/12" : "hover:bg-white/4"
                      }`}
                      onClick={() => toggle(m.transaction_id)}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(m.transaction_id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 rounded accent-emerald-500 shrink-0"
                        data-testid={`similar-checkbox-${m.transaction_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-heading font-bold text-white truncate">
                          {m.merchant || m.description}
                        </div>
                        <div className="text-[10px] font-mono text-white/40 flex items-center gap-1.5 mt-0.5">
                          <span>{m.date}</span>
                          <span className="text-white/20">·</span>
                          <span>
                            Actual: {categoryLookup?.[m.category]?.label || m.category || "sin"}
                          </span>
                          {m.reason === "merchant" ? (
                            <span className="ml-1 px-1.5 py-0.5 rounded on-solid bg-emerald-700 text-white text-[9px]">
                              100% merchant
                            </span>
                          ) : (
                            <span className="ml-1 px-1.5 py-0.5 rounded bg-white/8 text-white/60 text-[9px]">
                              {Math.round(m.score * 100)}% similar
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`text-sm font-mono font-bold shrink-0 tabular-nums ${
                          m.amount >= 0 ? "text-emerald-400" : "text-white"
                        }`}
                      >
                        {m.amount >= 0 ? "+" : ""}${Math.abs(m.amount || 0).toFixed(2)}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/8 flex items-center justify-between gap-2 bg-black/20">
          <div className="text-[11px] font-mono text-white/40">
            Solo cambiará las <b className="text-white/80">{selected.size}</b> seleccionadas
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-white/60 hover:text-white text-xs font-mono uppercase tracking-widest"
            >
              Cancelar
            </button>
            <button
              data-testid="similar-apply"
              onClick={applyCategory}
              disabled={applying || selected.size === 0}
              className="on-solid px-4 py-2 rounded-lg bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1.5"
            >
              {applying ? (
                <ArrowClockwise size={12} weight="bold" className="animate-spin" />
              ) : (
                <Check size={12} weight="bold" />
              )}
              Aplicar a {selected.size}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
